import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export default function Hero() {
  const { t } = useTranslation();
  const [current, setCurrent] = useState(0);
  const [loaded, setLoaded] = useState<Record<number, boolean>>({});

  const slides = [
    {
      image: 'https://www.can-el.com/common/img/hero1.jpg',
      tag: t('hero_slide1_tag'),
      title: t('hero_slide1_title'),
      subtitle: t('hero_slide1_subtitle'),
    },
    {
      image: 'https://www.can-el.com/common/img/hero2.jpg',
      tag: t('hero_slide2_tag'),
      title: t('hero_slide2_title'),
      subtitle: t('hero_slide2_subtitle'),
    },
    {
      image: 'https://www.can-el.com/common/img/hero3.jpg',
      tag: t('hero_slide3_tag'),
      title: t('hero_slide3_title'),
      subtitle: t('hero_slide3_subtitle'),
    },
  ];

  // Tüm resimleri önceden yükle (preload)
  useEffect(() => {
    slides.forEach((slide, idx) => {
      const img = new Image();
      img.src = slide.image;
      img.onload = () => setLoaded((prev) => ({ ...prev, [idx]: true }));
      img.onerror = () => setLoaded((prev) => ({ ...prev, [idx]: true }));
    });
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % slides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [slides.length]);

  return (
    <section className="relative w-full h-[100vh] min-h-[600px] overflow-hidden bg-[#1A2332]">
      {slides.map((slide, idx) => (
        <div
          key={idx}
          className={`absolute inset-0 transition-opacity duration-1000 ${idx === current ? 'opacity-100' : 'opacity-0'}`}
        >
          {loaded[idx] && (
            <img
              src={slide.image}
              alt={slide.tag}
              className="w-full h-full object-cover object-top"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-black/20" />
        </div>
      ))}

      {/* Content */}
      <div className="relative z-10 h-full flex flex-col justify-center px-6 md:px-16 max-w-7xl mx-auto w-full">
        <div className="max-w-2xl">
          <span className="inline-block bg-[#E8A020] text-white text-xs font-bold tracking-widest uppercase px-4 py-1.5 rounded-full mb-6">
            {slides[current].tag}
          </span>
          <h1 className="text-4xl md:text-6xl font-black text-white leading-tight mb-6">
            {slides[current].title.split('\n').map((line, i, arr) => (
              <span key={i}>
                {line}
                {i < arr.length - 1 && <br />}
              </span>
            ))}
          </h1>
          <p className="text-white/80 text-lg md:text-xl leading-relaxed mb-10 max-w-xl">
            {slides[current].subtitle}
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <a
              href="/projects"
              className="bg-[#E8A020] text-white font-bold px-8 py-4 rounded-full hover:bg-[#d4911a] transition-colors cursor-pointer whitespace-nowrap text-center"
            >
              {t('hero_cta_projects')}
            </a>
            <a
              href="/contact"
              className="border-2 border-white text-white font-bold px-8 py-4 rounded-full hover:bg-white hover:text-[#1A2332] transition-colors cursor-pointer whitespace-nowrap text-center"
            >
              {t('hero_cta_contact')}
            </a>
          </div>
        </div>
      </div>

      {/* Slide indicators */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10 flex gap-3">
        {slides.map((_, idx) => (
          <button
            key={idx}
            onClick={() => setCurrent(idx)}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              idx === current ? 'w-10 bg-[#E8A020]' : 'w-4 bg-white/50'
            }`}
          />
        ))}
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-10 right-10 z-10 hidden md:flex flex-col items-center gap-2">
        <span className="text-white/50 text-xs tracking-widest uppercase rotate-90 origin-center mb-4">Scroll</span>
        <div className="w-px h-12 bg-white/30" />
      </div>
    </section>
  );
}
